import styled from 'styled-components';
import { TableEvenOdd } from '../../../../../account/styles';
import { Button, message, Pagination } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Content } from 'antd/es/layout/layout';
import StatusDropdownTag from '../../../../../account/SMSPackage/ExtendHistoryTable/StatusDropdownTag';
import { IRequestSMSPurchase, IRequestSMSPurchaseParams, IResponseDataTable } from '../../../../interfaces/apis';
import Box from '../../../../../../components/Box';
import IcEdit from '../../../../../../components/Icon/icons/IcEdit';
import IcTrash from '../../../../../../components/Icon/icons/IcTrash';
import TopUpAmountReportModal, { TopUpAmountReportModalRef } from '../TopUpAmountReportModal';
import { useRef } from 'react';
import ModalConfirm, { ModalConfirmRef } from '../../../../../employees/components/ModalConfirm';
import { AxiosResponse } from 'axios';
import accountApis from '../../../../../../services/accountApis';
import { getMessageError } from '../../../../../../utils/response';
import { formatCurrency } from '../../../../../../utils/formatCurrency';
import { useNavigate } from 'react-router-dom';
import { formatPhone } from '../../../../../../utils/formatPhone';

interface IRequestSmsTableProps {
  data: IResponseDataTable<IRequestSMSPurchase> | undefined;
  params: IRequestSMSPurchaseParams;
  loading?: boolean;
  setParams: (params: IRequestSMSPurchaseParams) => void;
}

const RequestSmsTable = ({ data, params, loading, setParams }: IRequestSmsTableProps) => {
  const useTopUpAmountReportModalRef = useRef<TopUpAmountReportModalRef>(null);
  const modalConfirmRef = useRef<ModalConfirmRef>(null);
  const navigate = useNavigate();

  const handleUpdateStatus = async (id: string, newStatus: string) => {
    try {
      const res: AxiosResponse<{ data: any }> = await accountApis.updateStatusTopUp({
        id,
        status: newStatus,
      });

      if (res.data.data) {
        message.success('Status updated successfully');
        setParams({ ...params });
      }
    } catch (error) {
      message.error(getMessageError(error) || 'Failed to update status');
    }
  };
  const columns: ColumnsType<any> = [
    {
      title: 'Request Time',
      dataIndex: 'createdDate',
      className: 'createdDate',
    },
    {
      title: 'Salon Name',
      dataIndex: 'businessName',
      className: 'businessName',
      render: (value: string, record: IRequestSMSPurchase) => {
        return (
          <Button type='link' onClick={() => navigate(`/salon/${record.shopId}`)} style={{ padding: 0 }}>
            {value}
          </Button>
        );
      },
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      className: 'phone',
      render: (value: string) => {
        return formatPhone(value);
      },
    },
    {
      title: 'Total Cost',
      dataIndex: 'amount',
      className: 'amount',
      render: (value: number) => {
        return formatCurrency(value);
      },
    },
    {
      title: 'Note',
      dataIndex: 'note',
      className: 'note',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      className: 'status',
      render: (status: string, record: IRequestSMSPurchase) => (
        <StatusDropdownTag
          value={status as any}
          onChange={(newStatus) => {
            handleUpdateStatus(record.id, newStatus);
          }}
        />
      ),
    },
    {
      title: 'Actions',
      className: 'action',
      render: (status: string, record: IRequestSMSPurchase) => (
        <Box display='flex' gap={1} justifyContent='center' alignItems='center'>
          <Box
            width='32px'
            height='32px'
            bgColor='blue'
            borderRadius='4px'
            display='flex'
            justifyContent='center'
            alignItems='center'
            onClick={() => handleEditTopupAmount(record)}
          >
            <IcEdit fill='#FFF' />
          </Box>
          <Box
            width='32px'
            height='32px'
            bgColor='error_6'
            borderRadius='4px'
            display='flex'
            justifyContent='center'
            alignItems='center'
            onClick={() => handleDeleteTopupAmount(record)}
          >
            <IcTrash fill='#FFF' />
          </Box>
        </Box>
      ),
    },
  ];

  const handleChangePage = (newPage: number) => {
    setParams({ ...params, page: newPage });
  };

  const handleEditTopupAmount = (record: IRequestSMSPurchase) => {
    if (useTopUpAmountReportModalRef.current) {
      useTopUpAmountReportModalRef.current.open({
        shopId: record.shopId,
        amount: record.amount,
        note: record.note,
        id: record.id,
      });
    }
  };

  const onDeleteTopupAmount = async (record: IRequestSMSPurchase) => {
    try {
      const res: AxiosResponse<{ data: boolean }> = await accountApis.deleteTopUpSMS(record.id);

      if (res?.data?.data) {
        message.success('Delete top up amount successfully');
        setParams({ ...params });
        modalConfirmRef.current?.close();
      }
    } catch (error) {
      message.error(getMessageError(error, 'Failed to delete top up amount'));
    }
  };

  const handleDeleteTopupAmount = (record: IRequestSMSPurchase) => {
    modalConfirmRef.current?.open('Do you want delete this request top up?', 'You can’t restore this request', () => {
      onDeleteTopupAmount(record);
    });
  };

  return (
    <Container>
      <TableContentStyled
        loading={loading}
        columns={columns}
        dataSource={data?.content || []}
        rowKey='id'
        pagination={false}
      />

      <PagingStyled>
        <Pagination
          current={params.page}
          total={data?.totalElements}
          pageSize={10}
          onChange={handleChangePage}
          showSizeChanger={false}
        />
      </PagingStyled>
      <TopUpAmountReportModal
        ref={useTopUpAmountReportModalRef}
        onSuccess={() => setParams({ ...params })}
        isEdit={true}
      />
      <ModalConfirm ref={modalConfirmRef} />
    </Container>
  );
};

export default RequestSmsTable;

const Container = styled.div`
  flex: 1;
  width: 100%;
  gap: 16px;
  display: flex;
  flex-direction: column;
`;

const TableContentStyled = styled(TableEvenOdd)`
  && .dateTime {
    width: 20%;
  }

  && .amount {
    width: 10%;
  }

  && .note {
    width: 15%;
  }

  && .type {
    width: 10%;
  }

  && .processedDate {
    width: 15%;
  }

  && .status {
    width: 8%;
  }

  && .action {
    width: 5%;
  }

  && .progress {
    background-color: #eee;
    border-radius: 10px;
    display: inline-flex;
    font-size: 0.75rem;
    height: 20px;
    line-height: 0;
    overflow: hidden;
    position: relative;
    width: 104px;
  }

  && .progress span {
    color: rgba(245, 118, 127, 1);
    font-size: 12px;
    font-weight: 700;
    left: 0;
    position: absolute;
    right: 0;
    top: 50%;
    text-align: center;
  }

  && .progress-bar {
    background-color: rgba(255, 229, 231, 1);
    color: #fff;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    text-align: center;
    transition: width 0.6s ease;
    white-space: nowrap;
  }

  && .coupon-label-status {
    border-radius: 2px;
    color: #1d2129;
    font-size: 14px;
    font-weight: 500;
    gap: 10px;
    height: 28px;
    padding: 4px 8px;
    width: 60px;
  }

  && .btn-delete,
  .btn-edit {
    background: transparent;
    border: none;
    padding: 8px;
  }

  && .btn-delete:hover {
    background: rgba(245, 118, 127, 1) !important;
    border: none !important;
  }

  && .btn-edit:hover {
    background: var(--blue, #008bff) !important;
    border: none !important;
  }

  thead > tr > th.ant-table-cell {
    padding: 8px 16px;
    p {
      margin: 0;
    }
  }
`;

const PagingStyled = styled(Content)`
  text-align: right;
  margin-top: 10px;
`;
